import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Group, Stack } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPreviewCleanedCell } from '@feathr/blackbox';
import { defaultPersonAttributes, FieldCollection } from '@feathr/blackbox';
import { Button, Text } from '@feathr/components';
import CustomFieldSelect from '@feathr/extender/components/CustomFieldSelect';
import personAttributeOptions from '@feathr/extender/components/CustomFieldSelect/DataRequestPersonAttributes';
import { useStore } from '@feathr/extender/state';
import { fieldDataTypeLabel } from '@feathr/extender/styles/fieldDataType';

import CreateImportFieldModal from './CreateImportFieldModal';
import {
  fillPlaceholders,
  formatCleanedValue,
  matchBuiltInField,
  SAMPLE_DISPLAY_LENGTH,
  truncateSample,
} from './DataImportStepTwo.utils';
import type { IImportMappingCellProps } from './ImportMappingTable.utils';

import * as styles from './ImportMappingTable.css';

/** Which way the create-field modal opened: a fresh field, or the row's existing new field. */
type TModalMode = 'create' | 'edit';

interface IProps extends IImportMappingCellProps {
  /** A value from this column the field's type could not read, if the preview found one. */
  unreadableValue?: string;
  /** The first cell of this column whose symbols the preview stripped to read it as a number. */
  cleanedCell?: IPreviewCleanedCell;
}

/**
 * The mapping row's Feathr field. Creating a field opens a modal rather than inline inputs, and a
 * new, unsaved field can be edited from the pencil beside it.
 */
function ImportMappingFieldCell({
  column,
  importer,
  disabled,
  unreadableValue,
  cleanedCell,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { CustomFields } = useStore();
  const [modalMode, setModalMode] = useState<TModalMode | undefined>(undefined);

  // A column named for a built-in field (e.g. "email") starts out mapped to it.
  useEffect(() => {
    if (disabled) {
      return;
    }
    matchBuiltInField(column);
    importer.setAttributeDirty('column_mappers');
    // Not on `disabled`: a column is only auto-mapped when its row mounts.
  }, [column, importer]);

  const fieldId = column.feathr_attr;
  /*
   * Any default attribute id, opt-out included, is never a custom field: looking one up in
   * CustomFields would create a placeholder model and request it from the API.
   */
  const isDefaultAttribute = !!fieldId && defaultPersonAttributes.some(({ id }) => id === fieldId);
  const customField = fieldId && !isDefaultAttribute ? CustomFields.get(fieldId) : undefined;
  const newField = customField?.isEphemeral ? customField : undefined;
  // The select renders this list, so naming the field from blackbox's would contradict it.
  const fieldName = isDefaultAttribute
    ? personAttributeOptions.find(({ id }) => id === fieldId)?.u_key
    : customField?.get('u_key');
  /*
   * A custom field resolves after the first render, and a nameless warning reads "without .".
   * `-` keeps the field and type unescaped, which i18next would otherwise write as `N&#x2F;A`.
   */
  const warning =
    unreadableValue && fieldName && column.attr_type
      ? fillPlaceholders(
          t(
            "{{value}} isn't a valid {{- type}} value. Rows with values like this import without {{- field}}.",
            {
              type: fieldDataTypeLabel(column.attr_type, t),
              field: fieldName,
            },
          ),
          { value: truncateSample(unreadableValue, SAMPLE_DISPLAY_LENGTH) },
        )
      : undefined;

  const cleanedNote = cleanedCell
    ? fillPlaceholders(
        t('{{original}} will import as {{value}}. Symbols such as $ and commas are removed.'),
        {
          original: truncateSample(cleanedCell.original, SAMPLE_DISPLAY_LENGTH),
          value: formatCleanedValue(cleanedCell.value),
        },
      )
    : undefined;

  function openCreate(): void {
    setModalMode('create');
  }

  function openEdit(): void {
    setModalMode('edit');
  }

  function closeModal(): void {
    setModalMode(undefined);
  }

  return (
    <Stack gap={'xs'}>
      <Group gap={'xs'} wrap={'nowrap'}>
        <Box flex={1} miw={0}>
          <CustomFieldSelect
            contexts={[FieldCollection.Person]}
            customField={customField}
            disabled={disabled}
            isOptOut={importer.isOptOut}
            // Table cells clip overflow, so the menu renders on the body.
            menuPortalTarget={document.body}
            object={column}
            onRequestCreate={openCreate}
            showValueIcon={true}
          />
        </Box>
        {newField && (
          <Button
            aria-label={t('Edit new field')}
            disabled={disabled}
            onClick={openEdit}
            tooltip={t('Edit new field')}
            type={'icon'}
          >
            <FontAwesomeIcon icon={faPen} />
          </Button>
        )}
        {modalMode && (
          <CreateImportFieldModal
            column={column}
            // "Create new field" makes another field even when the row already has a new one.
            customField={modalMode === 'edit' ? newField : undefined}
            importer={importer}
            onClose={closeModal}
            opened={true}
          />
        )}
      </Group>
      {warning && (
        <Text.SmallRegular className={styles.warning} truncate={false}>
          {warning}
        </Text.SmallRegular>
      )}
      {cleanedNote && (
        <Text.SmallRegular className={styles.note} truncate={false}>
          {cleanedNote}
        </Text.SmallRegular>
      )}
    </Stack>
  );
}

export default observer(ImportMappingFieldCell);
